import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { Observable, from, throwError } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { getSilentRedirectUri, SessionTimerService, TenantContextService } from 'shared/core';
import { KF_AUTH_CONFIG } from './kf-auth.config';

export const InterceptorSkipHeader = 'X-Skip-Interceptor';

/**
 * The one HTTP interceptor every KanzenFlow vertical runs: it says which tenant
 * a request is for, attaches the bearer token, keeps the sliding session window
 * in step with the backend, and decides what a 401 means.
 *
 * This is rexine's `MyInterceptor`, moved rather than reimplemented, so a rexine
 * user sees the same behaviour they always have:
 *
 *  • `X-Skip-Interceptor` forwards the request untouched (minus the header).
 *  • Tenant headers go on every request, signed in or not — an unauthenticated
 *    call still has to say which tenant it is about.
 *  • The token is acquired silently on every request; MSAL answers from its
 *    cache when the token is still good, so this is not a round trip per call.
 *    `getSilentRedirectUri()` points the renewal iframe at a blank page, because
 *    loading the application into an iframe trips MSAL's `block_iframe_reload`.
 *  • A backend 401 buys exactly one retry with `forceRefresh`, on the theory
 *    that the cached token was stale. A second 401 means the session is over:
 *    clear the MSAL cache and go to the sign-in page.
 *  • `interaction_required` / `login_required` from the silent acquisition mean
 *    the same thing, and are treated the same way.
 *  • `X-Session-Expires` on any response advances the session timer, which is
 *    how the backend's sliding window reaches the countdown in the shell.
 *
 * The only thing that changed on the way in is where the values come from: the
 * API scope arrives through KF_AUTH_CONFIG instead of `src/environments`, which
 * a library cannot reach.
 */
@Injectable()
export class KfInterceptor implements HttpInterceptor {
  /** Proves at runtime that the interceptor actually ran. */
  static invocations = 0;

  private readonly msal = inject(MsalService);
  private readonly router = inject(Router);
  private readonly tenantContext = inject(TenantContextService);
  private readonly sessionTimer = inject(SessionTimerService);
  private readonly config = inject(KF_AUTH_CONFIG);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    KfInterceptor.invocations++;

    if (req.headers.has(InterceptorSkipHeader)) {
      return next.handle(req.clone({ headers: req.headers.delete(InterceptorSkipHeader) }));
    }

    const tenantHeaders = this.getTenantHeaders();

    // Unauthenticated requests still carry tenant context, matching the host.
    if (!this.msal.instance.getActiveAccount()) {
      return next.handle(req.clone({ setHeaders: { ...tenantHeaders } }));
    }

    return this.acquireTokenAndForward(req, next, tenantHeaders, false);
  }

  /**
   * Acquires a token from MSAL and forwards the request. `forceRefresh` bypasses
   * MSAL's cache and is only ever set by the single 401 retry below.
   */
  private acquireTokenAndForward(
    req: HttpRequest<any>,
    next: HttpHandler,
    tenantHeaders: Record<string, string>,
    forceRefresh: boolean
  ): Observable<HttpEvent<any>> {
    const account = this.msal.instance.getActiveAccount()!;

    return from(
      this.msal.acquireTokenSilent({
        scopes: [this.config.apiScope],
        account,
        redirectUri: getSilentRedirectUri(),
        forceRefresh,
      })
    ).pipe(
      mergeMap((result: AuthenticationResult) =>
        next
          .handle(
            req.clone({
              setHeaders: {
                Authorization: `Bearer ${result.accessToken}`,
                ...tenantHeaders,
              },
            })
          )
          .pipe(
            tap((event) => {
              if (event instanceof HttpResponse) {
                const sessionExpires = event.headers.get('X-Session-Expires');
                if (sessionExpires) {
                  this.sessionTimer.updateFromHeader(sessionExpires);
                }
              }
            }),
            catchError((error: unknown) => {
              if (error instanceof HttpErrorResponse && error.status === 401) {
                if (!forceRefresh) {
                  return this.acquireTokenAndForward(req, next, tenantHeaders, true);
                }
                this.endSession();
              }
              return throwError(() => error);
            })
          )
      ),
      catchError((error: unknown) => {
        const code = (error as { errorCode?: string })?.errorCode;
        if (code === 'interaction_required' || code === 'login_required') {
          this.endSession();
        }
        return throwError(() => error);
      })
    );
  }

  /** No token this session can recover: forget it and ask for a new sign-in. */
  private endSession(): void {
    this.msal.instance.clearCache();
    void this.router.navigate(['/login']);
  }

  private getTenantHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    const tenantId = this.tenantContext.getTenantId();
    if (tenantId) {
      headers['X-Tenant-Id'] = tenantId.toString();
    }

    const oldUserId = this.tenantContext.getOldUserId();
    if (oldUserId) {
      headers['X-Old-User-Id'] = oldUserId.toString();
    }

    return headers;
  }
}
